<template>
  <div class="why-us-page">
    <section class="page-hero why-us-hero">
      <div class="container why-us-hero__shell">
        <div class="why-us-hero__summary-bar">
          <span class="eyebrow">{{ content.eyebrow }}</span>
          <p class="page-copy">{{ content.title }}</p>
          <p class="why-us-hero__note">{{ content.intro }}</p>
        </div>
      </div>
    </section>

    <section class="section why-us-grid-section">
      <div class="why-us-grid__viewport">
        <div class="why-us-grid">
          <article
            v-for="(card, index) in content.cards"
            :key="card.title"
            :class="[
              'card-panel why-us-card',
              { 'why-us-card--featured': index === 0 }
            ]"
          >
            <div class="why-us-card__image-wrap">
              <img :src="card.image" :alt="card.alt" class="why-us-card__image">
            </div>
            <div class="why-us-card__body">
              <h2>{{ card.title }}</h2>
              <p v-for="line in card.body" :key="line">{{ line }}</p>
              <p v-if="card.emphasis" class="why-us-card__emphasis">{{ card.emphasis }}</p>
            </div>
            <span class="why-us-card__index">0{{ index + 1 }}</span>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
var whyUsContent = {
  en: {
    eyebrow: 'WHY US',
    title: 'Why clients choose JBC TOURS for agile planning, multilingual support, and purpose-built travel programs across Japan.',
    intro: 'We combine flexible thinking, responsive communication, and nationwide operating strength to shape inbound journeys that are refined in presentation and reliable in execution.',
    cards: [
      {
        image: '/images/whyus1.jpg',
        alt: 'Agile and creative travel planning team',
        title: 'Agile and creative team',
        body: [
          'Our team is composed of young, dynamic professionals who bring flexibility, speed, and creativity to every project.',
          'We go beyond conventional frameworks to design optimal travel solutions tailored to each client’s needs.',
          'With a strong sense of agility, we respond quickly and effectively to the ever-changing demands of the inbound travel market.'
        ],
        emphasis: 'We can respond to quotation requests on the same day at the earliest, and generally provide our proposals within 1-3 business days.'
      },
      {
        image: '/images/whyus2.jpg',
        alt: 'Dedicated multilingual client service in Japan',
        title: 'Dedicated client service with no compromise on quality',
        body: [
          'We provide seamless communication through multilingual support, ensuring smooth coordination across all stages of the journey.',
          'Leveraging our nationwide network of bus companies, hotels, and professional guides across Japan, we are able to design travel experiences that go far beyond major cities.',
          'Most importantly, we do not simply propose itineraries. We continue refining every detail until our clients are fully satisfied.',
          'From planning to execution, we act as a committed partner throughout the entire process.'
        ]
      },
      {
        image: '/images/whyus3.jpg',
        alt: 'Expertise in diverse travel programs across Japan',
        title: 'Expertise in diverse travel programs',
        body: [
          'We have extensive experience in business travel, inspection tours, large-scale group arrangements, and cruise-related itineraries.',
          'Our strength lies in designing purpose-driven programs such as incentive tours and corporate visits, tailored precisely to the objectives of each client.',
          'In addition, we offer fully flexible customization for FIT (Free Independent Traveler) arrangements, responding to even the most detailed requests with precision and care.'
        ]
      }
    ]
  },
  ja: {
    eyebrow: '当社の強み',
    title: '柔軟な企画力、きめ細かな対応力、そして全国ネットワークで訪日旅行を支えるJBC TOURSの強み。',
    intro: '企画初期のご相談から実施運営まで、一貫して伴走できる体制で、目的に沿ったインバウンドプログラムを構築します。',
    cards: [
      {
        image: '/images/whyus1.jpg',
        alt: '柔軟性と発想力を兼ね備えたチーム',
        title: '柔軟性と発想力を兼ね備えたチーム',
        body: [
          '若く新進気鋭のメンバーを中心に構成されたチームが、従来の枠にとらわれない柔軟な発想で、常に最適なプランを創出します。',
          'スピード感と対応力を強みに、多様化するインバウンドニーズに対して、機動的かつクリエイティブに応えます。'
        ],
        emphasis: 'お見積もりのリクエストには最短即日対応可、平均で1〜3営業日以内にご回答いたします。'
      },
      {
        image: '/images/whyus2.jpg',
        alt: '納得いただくまで寄り添う顧客対応力',
        title: '納得いただくまで寄り添う顧客対応力',
        body: [
          '多言語対応スタッフによる円滑なコミュニケーションに加え、日本全国に広がるバス会社・ホテル・通訳ガイドとのネットワークを活かし、都市部に限らない幅広い手配が可能です。',
          'また、私たちは単にプランを提示するだけでなく、お客様がご納得されるまで徹底的にプランを磨き上げることを重視しています。',
          '企画段階から実施まで、一貫して伴走するパートナーとしてサポートいたします。'
        ]
      },
      {
        image: '/images/whyus3.jpg',
        alt: '多様なニーズに応える企画・運営力',
        title: '多様なニーズに応える企画・運営力',
        body: [
          'ビジネス渡航や視察・研修旅行をはじめ、大規模団体やクルーズツアーの対応実績も豊富に有しています。',
          'インセンティブツアーなど目的志向のプログラム設計にも強みを持ち、クライアントの意図に沿った最適な旅行を実現します。',
          'また、個人旅行（FIT）においても、細かなご要望に応じた柔軟なカスタマイズが可能です。'
        ]
      }
    ]
  }
};

export default {
  name: 'WhyUs',
  props: {
    language: {
      type: String,
      default: 'en'
    }
  },
  computed: {
    content: function () {
      return whyUsContent[this.language] || whyUsContent.en;
    }
  }
};
</script>

<style scoped>
.why-us-hero {
  padding-bottom: 24px;
}

.why-us-hero__shell {
  display: grid;
}

.why-us-hero__summary-bar {
  display: grid;
  gap: 8px 18px;
  grid-template-columns: auto minmax(0, 1.2fr) minmax(280px, 0.9fr);
  align-items: center;
  max-width: 1180px;
  padding: 18px 24px;
  border-radius: 18px;
  border: 1px solid rgba(15, 76, 129, 0.08);
  background: linear-gradient(90deg, rgba(15, 76, 129, 0.05), rgba(255, 255, 255, 0.92));
}

.why-us-hero__summary-bar .page-copy {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.7;
}

.why-us-hero__note {
  margin: 0;
  color: var(--color-muted);
  line-height: 1.7;
}

.why-us-grid-section {
  padding-top: 0;
}

.why-us-grid__viewport {
  width: min(calc(100% - 32px), 1480px);
  margin: 0 auto;
}

.why-us-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  align-items: stretch;
}

.why-us-card {
  position: relative;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 22px;
  padding: 24px;
  overflow: hidden;
}

.why-us-card--featured {
  background: linear-gradient(180deg, rgba(15, 76, 129, 0.08), rgba(255, 255, 255, 0.98));
}

.why-us-card__image-wrap {
  overflow: hidden;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.72);
}

.why-us-card__image {
  width: 100%;
  height: 260px;
  object-fit: cover;
  display: block;
}

.why-us-card__body {
  display: grid;
  align-content: start;
  gap: 12px;
}

.why-us-card__kicker {
  display: inline-flex;
  width: fit-content;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgba(15, 76, 129, 0.08);
  color: var(--color-accent-deep);
  font-size: 12px;
  letter-spacing: 0.08em;
}

.why-us-card__body h2 {
  margin: 0;
  padding-right: 52px;
  font-size: 24px;
  line-height: 1.4;
}

.why-us-card__body p {
  margin: 0;
  color: var(--color-muted);
  line-height: 1.85;
}

.why-us-card__emphasis {
  margin-top: 4px;
  color: var(--color-accent-deep);
  font-weight: 600;
}

.why-us-card__index {
  position: absolute;
  top: 18px;
  right: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  height: 42px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(15, 76, 129, 0.1);
  color: var(--color-accent-deep);
  font-size: 12px;
  letter-spacing: 0.1em;
}

@media (max-width: 1023px) {
  .why-us-hero__summary-bar {
    grid-template-columns: 1fr;
    align-items: start;
  }

  .why-us-grid__viewport {
    width: min(calc(100% - 32px), 100%);
  }

  .why-us-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .why-us-hero__summary-bar,
  .why-us-card {
    padding: 22px;
  }

  .why-us-grid__viewport {
    width: min(calc(100% - 24px), 100%);
  }

  .why-us-card__image {
    height: 220px;
  }

  .why-us-card__body h2 {
    padding-right: 44px;
    font-size: 22px;
  }
}
</style>
